import React, { useEffect, useState, useCallback } from "react";
import { supabase, registrarActividad } from "../supabaseClient";
import { useAuth } from "../context/AuthContext";
import { useRealtime } from "../hooks/useRealTime"; // ✅ Importamos el hook estandarizado
import "../styles/FileManager.css";

const FileManager = ({ tableName, folderName, isAdmin }) => {
    const [file, setFile] = useState(null);
    const [docsList, setDocsList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [feedback, setFeedback] = useState({ msg: "", type: "" });
    const { user } = useAuth();

    // 1. Carga inicial de documentos (Solo se ejecuta una vez por tabla)
    const fetchDocs = useCallback(async () => {
        try {
            setLoading(true);
            const { data, error } = await supabase
                .from(tableName)
                .select("*")
                .order("created_at", { ascending: false });

            if (!error) setDocsList(data || []);
        } finally {
            setLoading(false);
        }
    }, [tableName]);

    // ✅ 2. IMPLEMENTACIÓN DEL HOOK REAL-TIME
    // Esta lógica gestiona la lista sin necesidad de volver a consultar la DB completa
    useRealtime(tableName, useCallback((payload) => {
        if (payload.eventType === 'INSERT') {
            setDocsList(prev => {
                const exists = prev.some(doc => doc.id === payload.new.id);
                return exists ? prev : [payload.new, ...prev];
            });
        } else if (payload.eventType === 'DELETE') {
            setDocsList(prev => prev.filter(doc => doc.id !== payload.old.id));
        } else if (payload.eventType === 'UPDATE') {
            setDocsList(prev => prev.map(doc => doc.id === payload.new.id ? payload.new : doc));
        }
    }, []));

    useEffect(() => {
        fetchDocs();
    }, [fetchDocs]);

    const showFeedback = (msg, type) => {
        setFeedback({ msg, type });
        setTimeout(() => setFeedback({ msg: "", type: "" }), 4000);
    };

    const handleSubmit = async () => {
        if (!file || !user) return showFeedback("⚠️ Selecciona un archivo.", "error");

        try {
            setLoading(true);
            const fileName = `${Date.now()}_${file.name.replace(/\s+/g, '_')}`;
            const storagePath = `${folderName}/${fileName}`;

            // Subida al Storage
            const { error: uploadError } = await supabase.storage.from("archivos").upload(storagePath, file);
            if (uploadError) throw new Error(`Fallo en Storage: ${uploadError.message}`);

            // Registro en DB
            const { data: urlData } = supabase.storage.from("archivos").getPublicUrl(storagePath);
            const { error: insertError } = await supabase.from(tableName).insert([{
                name: file.name,
                url: urlData.publicUrl,
                storage_path: storagePath,
                uploaded_by: user.email
            }]);

            if (insertError) {
                await supabase.storage.from("archivos").remove([storagePath]); // Rollback manual
                throw new Error(`Fallo en Base de Datos: ${insertError.message}`);
            }

            await registrarActividad(user.email, `SUBIDA: ${file.name}`, { tabla: tableName });

            // ✅ IMPORTANTE: No llamamos a fetchDocs(). El Real-time insertará la fila sola.
            showFeedback("🚀 ¡Archivo cargado con éxito!", "success");
            setFile(null);
            if(document.getElementById("fileInput")) document.getElementById("fileInput").value = "";
        } catch (err) {
            showFeedback(err.message, "error");
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (item) => {
        if (!window.confirm(`¿Eliminar "${item.name}"?`)) return;
        try {
            setLoading(true);

            // 1. Eliminar de Storage
            const { error: storageError } = await supabase.storage.from("archivos").remove([item.storage_path]);
            if (storageError) throw new Error("No se pudo borrar el archivo físico.");

            // 2. Eliminar de DB
            const { error: dbError } = await supabase.from(tableName).delete().eq("id", item.id);
            if (dbError) throw dbError;

            // ✅ El Real-time quitará el archivo de la lista automáticamente
            showFeedback("🗑️ Archivo eliminado correctamente.", "success");
            await registrarActividad(user.email, `Eliminó: ${item.name}`);
        } catch (err) {
            showFeedback(`❌ Error: ${err.message}`, "error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="modern-file-manager">
            {feedback.msg && (
                <div className={`notification-toast ${feedback.type}`}>
                    {feedback.msg}
                </div>
            )}

            {isAdmin && (
                <div className="upload-zone">
                    <h4>📤 Gestión de Documentación</h4>
                    <div className="input-group">
                        <input id="fileInput" type="file" accept="application/pdf" onChange={(e) => setFile(e.target.files[0])} className="custom-file-input" />
                        <button onClick={handleSubmit} disabled={loading || !file} className={`btn-primary ${loading ? 'loading' : ''}`}>
                            {loading ? "Subiendo..." : "Subir PDF"}
                        </button>
                    </div>
                </div>
            )}

            <div className="files-list">
                {docsList.length === 0 && !loading ? (
                    <p className="empty-msg">No hay documentos cargados en esta sección.</p>
                ) : (
                    docsList.map((item) => (
                        <div key={item.id} className="file-row">
                            <a href={item.url} target="_blank" rel="noreferrer" className="file-info">
                                <span className="icon">📄</span>
                                <div className="details">
                                    <span className="name">{item.name}</span>
                                    <span className="meta">Click para visualizar</span>
                                </div>
                            </a>
                            {isAdmin && (
                                <button onClick={() => handleDelete(item)} className="btn-delete" title="Eliminar" disabled={loading}>
                                    {loading ? "..." : "🗑️"}
                                </button>
                            )}
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default FileManager;